import axios, {
	type AxiosError,
	type AxiosRequestConfig,
	type AxiosResponse,
	type InternalAxiosRequestConfig,
	type ResponseType,
} from "axios";
import { cookies } from "./cookies";

const API_URL = process.env.REACT_APP_API_URL;
const API_URL_ONBOARDING = process.env.REACT_APP_API_URL_ONBOARDING;

interface Response {
	access: string;
	refresh: string;
}

const api = axios.create({
	baseURL: API_URL,
	// headers: {
	// 	"ngrok-skip-browser-warning": "true",
	// },
});

export const APIOnboarding = axios.create({
	baseURL: API_URL_ONBOARDING,
	headers: {
		"Content-Type": "application/json",
		Authorization: `Token ${process.env.REACT_APP_TOKEN_ONBOARDING}`,
	},
});

// Remove Content-Type fixo em FormData pra browser gerar o boundary multipart sozinho.
APIOnboarding.interceptors.request.use((config: InternalAxiosRequestConfig) => {
	if (config.data instanceof FormData && config.headers) {
		delete config.headers["Content-Type"];
	}
	return config;
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
	const access = cookies.get("@Checkauto:access");
	if (access) {
		config.headers = config.headers ?? {};
		if (!config.headers.Authorization) {
			config.headers.Authorization = `Bearer ${access}`;
		}
	}
	return config;
});

let isRefreshing = false;
let waitQueue: Array<(token: string | null) => void> = [];

const flushQueue = (token: string | null) => {
	waitQueue.forEach((fn) => fn(token));
	waitQueue = [];
};

const AUTH_ENDPOINTS = [
	"token/", // login
	"token/refresh/", // refresh em si
	"user/forgot-password", // forgot
	"user/change-password", // change
];

api.interceptors.response.use(
	(res) => res,
	async (error: AxiosError) => {
		const original = error.config as
			| (InternalAxiosRequestConfig & { _retry?: boolean })
			| undefined;
		const status = error.response?.status;
		const urlLower = (original?.url || "").toLowerCase();

		// Não tenta refresh no próprio /refresh/ nem repete request já re-tentada
		if (
			!original ||
			original._retry ||
			status !== 401 ||
			original.url?.includes("/refresh/")
		) {
			return Promise.reject(error);
		}

		if (AUTH_ENDPOINTS.some((p) => urlLower.includes(p))) {
			return Promise.reject(error);
		}

		original._retry = true;

		if (isRefreshing) {
			// Aguarda o refresh em andamento
			return new Promise((resolve, reject) => {
				waitQueue.push((newToken) => {
					if (!newToken) return reject(error);
					original.headers.set("Authorization", `Bearer ${newToken}`);
					resolve(api(original));
				});
			});
		}

		// Inicia um único refresh
		isRefreshing = true;
		try {
			const refresh = cookies.get("@Checkauto:refresh");
			if (!refresh) throw error;

			const { data } = await axios.post(
				`${api.defaults.baseURL}/token/refresh/`,
				{ refresh },
				{ headers: { "Content-Type": "application/json" } },
			);

			const newAccess = (data as Response)?.access;
			const newRefresh = (data as Response)?.refresh;
			if (!newAccess) throw new Error("Refresh não retornou access");

			cookies.set("@Checkauto:access", newAccess);
			cookies.set("@Checkauto:refresh", newRefresh);
			flushQueue(newAccess);
			isRefreshing = false;

			// Reexecuta a original com o novo token
			original.headers.set("Authorization", `Bearer ${newAccess}`);
			return api(original);
		} catch (e) {
			flushQueue(null);
			isRefreshing = false;

			cookies.remove("@Checkauto:user");
			cookies.remove("@Checkauto:access");
			cookies.remove("@Checkauto:refresh");
			window.location.replace("/");
			return Promise.reject(e);
		}
	},
);

const inflight = new Map<string, Promise<AxiosResponse<any>>>();
const _get = api.get.bind(api);

api.get = ((url: string, config?: AxiosRequestConfig) => {
	if (config && (config as any).noDedup) return _get(url, config);

	const key = `${url}|${config?.responseType ?? "json"}`;
	if (inflight.has(key)) return inflight.get(key)!;

	const p = _get(url, config).finally(() => inflight.delete(key));
	inflight.set(key, p);
	return p;
}) as typeof api.get;

export async function destroy(
	path: string,
	responseType: ResponseType = "json",
) {
	try {
		return await api.delete(path, { responseType });
	} catch (err: any) {
		return err?.response ?? err;
	}
}

export async function get<T = any>(
	path: string,
	responseType: ResponseType = "json",
): Promise<T> {
	try {
		const res = await api.get<T>(path, { responseType });
		return res.data;
	} catch (err: any) {
		if (err?.response?.data) return err.response.data as T;
		throw err;
	}
}

export async function put(
	path: string,
	data: object,
	responseType: ResponseType = "json",
) {
	try {
		return await api.put(path, data, { responseType });
	} catch (err: any) {
		return err?.response ?? err;
	}
}

export async function post(
	path: string,
	data: object,
	content_type?: string,
	responseType: ResponseType = "json",
) {
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};
	if (content_type) headers["Content-Type"] = content_type;

	try {
		return await api.post(path, data, { headers, responseType });
	} catch (err: any) {
		return err?.response ?? err;
	}
}

export async function patch(
	path: string,
	data: object,
	responseType: ResponseType = "json",
) {
	try {
		return await api.patch(path, data, { responseType });
	} catch (err: any) {
		return err?.response ?? err;
	}
}

// Instância crua do axios, com os interceptors de auth/refresh já plugados.
// Usada quando quem chama precisa do status HTTP de verdade (200/202/403/...),
// que os wrappers get/post acima descartam.
export { api as httpClient };

export const defaults: AxiosRequestConfig = {
	headers: {
		"Content-Type": "application/json",
	},
	responseType: "json",
};
